import { useQuery } from "@apollo/client"
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react"
import { GET_JOB_LISTING_QUERY } from "../entities/job/graphql/queries"
import { getCompletedJobGroups, splitJobIntoPickupAndDelivery } from "../entities/job/helper"
import { IJob, IShiftStopIds, IStop } from "../entities/job/interface"
import { GET_SHIFT_QUERY } from "../entities/shifts/graphql/queries"
import { IShift } from "../entities/users/interface"
import { IVehicle } from "../entities/vehicle/interface"
import { ENativeLayerOutgoingEventType } from "../utils/nativeLayerCommunication/interfaces"
import { sendNativeLayerEvent } from "../utils/nativeLayerCommunication/sendNativeLayerEvent"
import NativeLayerContext from "./NativeLayerContext"
import SiteContext from "../contexts/SiteContext"
import { GET_GEOFENCE_LIST_QUERY } from "../entities/tracking/graphql/queries"
import { IGeofence, IGeofenceCoordinates } from "../entities/tracking/geofences/interface"
import { checkGeofenceEvent, prepareBBox } from "../entities/tracking/geofences/GpsTracking"
import { useGeofenceEvent } from "../hooks/useGeofenceEvent"

interface IShiftProviderProps {
    reinitializeApolloClient: () => Promise<void>
    children: ReactNode
}

export interface IShiftContext {
    shiftId: string | null
    setShiftId: (shiftId: string) => Promise<void>
    vehicleId: string | null
    setVehicleId: (vehicleId: string) => Promise<void>
    vehicle: IVehicle | null
    setVehicle: (vehicle: IVehicle) => void
    selectedShift: IShift | null
    setSelectedShift: (shift: IShift) => Promise<void>
    setStops: React.Dispatch<React.SetStateAction<IShiftStopIds | null>>
    stops: IShiftStopIds | null
    updateJob: (job: IJob[]) => void
    jobs: { [key: string]: IJob } | null
    latestGPS: [number, number] | null
    setLatestGPS: (gps: [number, number] | null) => void
    geofences: IGeofence[]
    setGeofecnes: (geofences: IGeofence[]) => void
    completedJobs: { [key: string]: IJob } // The completed jobs
    setCompletedJobs: React.Dispatch<React.SetStateAction<{ [key: string]: IJob }>> // Setter for completed jobs
    completedJobIdsFromStorage: string[] // The completed job IDs from localStorage
}

const ShiftContext = createContext<IShiftContext | undefined>({} as IShiftContext)

export function ShiftProvider({ reinitializeApolloClient, children }: IShiftProviderProps) {
    const nativeLayerContext = useContext(NativeLayerContext)
    const [shiftId, setShiftId] = useState<string | null>(window.sessionStorage.getItem("shiftId") ?? null)
    const [vehicleId, setVehicleId] = useState<string | null>(window.sessionStorage.getItem("vehicleId") ?? null)
    const [vehicle, setVehicle] = useState<IVehicle | null>(null)
    const [selectedShift, setSelectedShift] = useState<IShift | null>(null)
    const [stops, setStops] = useState<IShiftStopIds | null>(null)
    const [jobs, setJobs] = useState<{ [key: string]: IJob } | null>(null) //a map of job ID to job
    const [latestGPS, setLatestGPS] = useState<[number, number] | null>(null)
    const [geofences, setGeofecnes] = useState<IGeofence[]>([])
    const [completedJobs, setCompletedJobs] = useState<{ [key: string]: IJob }>({})
    const [completedJobIdsFromStorage, setCompletedJobIdsFromStorage] = useState<string[]>([])
    const siteContext = useContext(SiteContext)
    const isSiteTierOne = siteContext?.site?.tier === "TIER1"
    const { trackGeofenceStatus } = useGeofenceEvent()

    const { data: shiftData } = useQuery(GET_SHIFT_QUERY, {
        variables: {
            id: shiftId,
        },
        skip: !shiftId,
    })

    useEffect(() => {
        const fetchData = async () => {
            try {
                const completedJobGroups = getCompletedJobGroups()
                if (completedJobGroups) {
                    const allCompletedJobIds = Object.values(completedJobGroups).flat() as string[] // Extract and flatten IDs
                    setCompletedJobIdsFromStorage(allCompletedJobIds)
                }
            } catch (error) {
                console.error("Error retrieving completed job IDs from localStorage:", error)
            }
        }
        fetchData()
    }, [completedJobs])

    useEffect(() => {
        if (shiftData?.shift) {
            setSelectedShift(shiftData.shift)
        }
    }, [shiftData])

    const { data: shiftJobs } = useQuery(GET_JOB_LISTING_QUERY, {
        variables: {
            filter: {
                assignedShift: {
                    vehicle: vehicleId,
                    shiftId: shiftId,
                },
                _operators: { jobStatusHistory: { status: { nin: ["completed", "canceled", "onHold"] } } },
            },
        },
        skip: !vehicleId || !shiftId || isSiteTierOne,
        fetchPolicy: "cache-and-network",
    })

    const { data: completedJobsFromQuery } = useQuery(GET_JOB_LISTING_QUERY, {
        variables: {
            filter: {
                _operators: { _id: { in: completedJobIdsFromStorage } }, // Filter for IDs not already in completedJobs
            },
        },
        skip: completedJobIdsFromStorage.length === 0,
        fetchPolicy: "cache-and-network",
    })

    useEffect(() => {
        if (completedJobsFromQuery?.jobsList?.records) {
            const newCompletedJobs = completedJobsFromQuery.jobsList.records.reduce(
                (acc: { [x: string]: any }, job: { _id: string | number }) => {
                    const completedJob = { ...job, isJobCompleted: true }

                    acc[completedJob._id] = completedJob
                    return acc
                },
                {}
            )
            // loop over newCompletedJobs and update job
            updateJob(Object.values(newCompletedJobs) as IJob[]) // Pass all jobs at once
            setCompletedJobs((prevCompleted) => ({ ...prevCompleted, ...newCompletedJobs }))
        }
    }, [completedJobsFromQuery])

    useEffect(() => {
        if (selectedShift && shiftJobs?.jobsList?.records) {
            const jobs = shiftJobs.jobsList.records
            const allJobs = { ...completedJobs } // Start with completed jobs
            jobs.forEach((job: IJob) => {
                allJobs[job._id] = job
            })

            setJobs(allJobs) // Set the combined jobs

            // Convert allJobs object to an array:
            const allJobsArray = Object.values(allJobs)

            const shiftStops = splitJobIntoPickupAndDelivery(selectedShift, allJobsArray).ids

            // Retrieve the groupId and destinationStops from sessionStorage
            const groupId = window.sessionStorage.getItem("groupId") ?? ""
            const destinationStops = JSON.parse(window.sessionStorage.getItem("destinationStops") ?? "[]")

            const destinationStopsFlat = shiftStops[groupId]?.destinationStops.flat()
            if (destinationStops.length > 0 && destinationStopsFlat) {
                // Create a map to track job statuses from session storage
                const jobMap = new Map<string, string>()
                destinationStopsFlat.forEach((job: IStop) => {
                    jobMap.set(`${job.jobId}-${job.type}`, job.status)
                })

                // Update statuses in storedDestinationStops
                const updatedDestinationStops = destinationStops.map((stopGroup: IStop[]) =>
                    stopGroup.map((stop: IStop) => ({
                        ...stop,
                        status: jobMap.get(`${stop.jobId}-${stop.type}`) || stop.status,
                    }))
                )

                window.sessionStorage.setItem("destinationStops", JSON.stringify(updatedDestinationStops))

                if (groupId && shiftStops[groupId] && destinationStops) {
                    const updatedStops = {
                        ...shiftStops,
                        [groupId]: {
                            ...shiftStops[groupId],
                            destinationStops: updatedDestinationStops, // Update stops with new statuses
                        },
                    }

                    setStops(updatedStops)
                }
            } else {
                setStops(shiftStops)
            }
        }
    }, [shiftJobs, selectedShift, completedJobs])

    // Get Geofences list
    const { data: geofenceData } = useQuery(GET_GEOFENCE_LIST_QUERY, {
        variables: {
            limit: 500,
        },
        skip: nativeLayerContext?.auth0IdToken !== null,
    })

    useEffect(() => {
        if (geofenceData?.geofencesList?.records) {
            setGeofecnes(geofenceData?.geofencesList?.records)
        }
    }, [geofenceData])

    useEffect(() => {
        if (!latestGPS || geofences.length === 0) return

        const geofenceCoordinates: IGeofenceCoordinates[] = geofences
            .map((geofence) => {
                if (!geofence._id) return null

                if (!geofence.geometry || !geofence?.geometry?.coordinates) return null

                const coordinates = geofence.geometry.coordinates[0]

                return {
                    id: geofence._id,
                    coordinates: coordinates as unknown as [number, number][],
                    entryNotifications: geofence.properties?.entryNotifications ?? false,
                    exitNotifications: geofence.properties?.exitNotifications ?? false,
                }
            })
            .filter((g): g is IGeofenceCoordinates => g !== null)

        const bBoxData = prepareBBox(geofenceCoordinates)

        if (vehicleId) {
            checkGeofenceEvent(bBoxData, latestGPS, vehicleId, trackGeofenceStatus)
        }
    }, [latestGPS, geofences, vehicleId, trackGeofenceStatus])

    useEffect(() => {
        if (nativeLayerContext.latestGPS) {
            _setLatestGPS(nativeLayerContext.latestGPS)
        } else if ("geolocation" in navigator) {
            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude } = position.coords
                    _setLatestGPS([latitude, longitude])
                },
                (error) => {
                    console.error("Error getting location:", error)
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 5000,
                    timeout: 10000,
                }
            )

            return () => navigator.geolocation.clearWatch(watchId)
        }
    }, [nativeLayerContext.latestGPS])

    const _setShiftId = async (shiftId: string) => {
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem("shiftId", shiftId)
        }
        await reinitializeApolloClient()
        setShiftId(shiftId)
        sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.SET_SHIFT, shiftId })
    }

    const _setVehicleId = async (vehicleId: string | null) => {
        if (vehicleId === null) {
            return
        }

        if (typeof window !== "undefined") {
            window.sessionStorage.setItem("vehicleId", vehicleId)
        }

        setVehicleId(vehicleId)
        sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.SET_VEHICLE, vehicleId })
    }

    const _setSelectedShift = async (shift: IShift) => {
        _setVehicleId(shift.vehicle._id)
        _setShiftId(shift._id)
        setSelectedShift(shift)
        await reinitializeApolloClient()
    }

    const updateJob = (jobs: IJob[]) => {
        setJobs((prev) => {
            if (!prev) return null

            const updatedJobs = { ...prev }

            jobs.forEach((job) => {
                if (updatedJobs[job._id]) {
                    updatedJobs[job._id] = { ...updatedJobs[job._id], ...job }
                }
            })

            return updatedJobs
        })
    }

    const _setLatestGPS = useCallback(
        (gps: [number, number] | null) => {
            setLatestGPS((prev) => {
                if (prev?.[0] !== gps?.[0] || prev?.[1] !== gps?.[1]) {
                    return gps
                }

                return prev
            })
        },
        [setLatestGPS]
    )

    const context: IShiftContext = {
        shiftId,
        setShiftId: _setShiftId,
        vehicleId,
        setVehicleId: _setVehicleId,
        vehicle,
        setVehicle,
        selectedShift,
        setSelectedShift: _setSelectedShift,
        setStops,
        stops,
        updateJob,
        jobs: jobs,
        latestGPS,
        setLatestGPS: _setLatestGPS,
        geofences,
        setGeofecnes,
        completedJobs,
        setCompletedJobs,
        completedJobIdsFromStorage,
    }

    return <ShiftContext.Provider value={context}>{children}</ShiftContext.Provider>
}

export default ShiftContext
